import type { ClientRef, HubClientSnapshot } from './types';
import type { WarehouseHubOption } from './WarehouseContext';

/**
 * The frozen hub / client snapshot every WMS create call carries (outbound
 * request, PO, CSV import, storage). One rule for all of them: the name falls
 * back to `Hub #id` when the hub list doesn't know it, and the code is
 * trimmed — blank means "no code" (null).
 */

export interface HubSnapshot {
  hubID: number;
  hubName: string;
  hubCode: string | null;
}

/** Trimmed hub code; blank → null. */
export const normalizeHubCode = (
  code: string | null | undefined
): string | null => code?.trim() || null;

/** Trimmed client (provider) code; blank → none. */
export const normalizeClientCode = (
  code: string | null | undefined
): string | undefined => code?.trim() || undefined;

export const buildHubSnapshot = (
  hubs: readonly WarehouseHubOption[],
  hubID: number
): HubSnapshot => {
  const hub = hubs.find((h) => h.id === hubID);
  return {
    hubID,
    hubName: hub?.name?.trim() || `Hub #${hubID}`,
    hubCode: normalizeHubCode(hub?.code),
  };
};

export const buildHubClientSnapshot = (
  hubs: readonly WarehouseHubOption[],
  hubID: number,
  client: ClientRef
): HubClientSnapshot => ({
  ...buildHubSnapshot(hubs, hubID),
  clientID: client.id,
  clientName: client.name,
  ...(normalizeClientCode(client.code)
    ? { clientCode: normalizeClientCode(client.code) }
    : {}),
});
